// TERMINE (P6) — der Metaordner „Termine" als eigene LISTENQUELLE.
//
// Einladungen, Zusagen, Absagen und Terminabsagen sind Mails wie alle anderen —
// nur liest sie niemand einzeln. Sie werden deshalb ZUSÄTZLICH gebündelt
// gezeigt, und zwar als reine SICHT: Es wird nichts im Postfach verschoben,
// nichts gelöscht, nichts umsortiert. Die Nachrichten stehen weiterhin im
// Posteingang; „Termine" zeigt dieselben Objekte ein zweites Mal.
//
// Vorbild ist `services/vorgangsMails.ts`: Auch dort baut eine kleine Datei aus
// einer Server-Antwort `Email`-Zeilen, damit die Liste identisch aussieht, egal
// woher eine Zeile stammt. Die Mapping-Funktion von `graphMailService` ist
// modul-privat; sie hier nachzubauen ist der Preis dafür, dass der Termine-Pfad
// die bestehende Mail-Liste nicht anfassen muss.
import { apiUrl } from "../../utils/api";
import { getDateLabel, stringToColor, type Email, type UnreadMeta } from "../../services/graphMailService";
import { cleanMailPreview } from "../../services/mailPreview";

/** Die fünf Arten, die Graph kennt — Spiegel von `MeetingMessageType` im Backend. */
export type MeetingMessageType =
    | "meetingRequest"
    | "meetingAccepted"
    | "meetingTentativelyAccepted"
    | "meetingDeclined"
    | "meetingCancelled";

/** Deutsche Beschriftung je Art (Spiegel von `TERMIN_ART_LABEL` im Backend). */
export const TERMIN_ART_LABEL: Readonly<Record<MeetingMessageType, string>> = {
    meetingRequest: "Einladung",
    meetingAccepted: "Zusage",
    meetingTentativelyAccepted: "Mit Vorbehalt",
    meetingDeclined: "Absage",
    meetingCancelled: "Termin abgesagt",
};

/**
 * Beschriftung einer Art. Unbekannte/fehlende Art → leerer String: lieber gar
 * kein Etikett als ein geratenes.
 */
export function terminArtLabel(art: MeetingMessageType | null | undefined): string {
    return art ? TERMIN_ART_LABEL[art] ?? "" : "";
}

/** Eine Zeile des Termine-Ordners: gewöhnliche Mail plus ihre Termin-Art. */
export type TerminMail = Email & {
    /** Postfach, gegen das gelesen wird (der Ordner ist postfach-gebunden). */
    srcMailbox: string;
    meetingMessageType: MeetingMessageType | null;
};

/** Rohform einer Listenzeile, wie das Backend sie liefert (Ausschnitt). */
export interface TerminMailDto {
    id: string;
    conversationId?: string;
    subject?: string;
    from?: { name?: string; address?: string } | null;
    toRecipients?: { name: string; address: string }[];
    ccRecipients?: { name: string; address: string }[];
    receivedDateTime: string;
    bodyPreview?: string;
    hasAttachments?: boolean;
    isRead?: boolean;
    parentFolderId?: string;
    matchedContact?: { id: number; displayName: string } | null;
    matchedContactIds?: number[];
    recognizedContact?: { id: number; displayName: string } | null;
    recognizedContactIds?: number[];
    senderCandidates?: Email["senderCandidates"];
    isInternalSender?: boolean;
    meetingMessageType?: MeetingMessageType | null;
}

/** Eine Seite des Termine-Ordners: Zeilen + Paginierungs-/Transparenz-Meta. */
export interface TermineSeite {
    mails: TerminMail[];
    meta: UnreadMeta;
}

/**
 * DTO → Listenzeile. Rein, ohne Netzzugriff.
 *
 * Der Rumpf bleibt leer wie in jeder Listenzeile — er wird erst beim Öffnen
 * nachgeladen. Die Erkennungsfelder werden mitgeführt, damit eine Termin-Zeile
 * denselben Kontaktbezug zeigt wie dieselbe Mail im Posteingang.
 */
export function dtoZuTerminMail(item: TerminMailDto, mailbox: string): TerminMail {
    const senderName = item.from?.name || item.from?.address || "Unbekannt";
    const date = new Date(item.receivedDateTime);
    const gueltig = !Number.isNaN(date.getTime());
    return {
        id: item.id,
        conversationId: item.conversationId || item.id,
        senderName,
        senderEmail: item.from?.address || "",
        senderInitial: senderName.charAt(0).toUpperCase() || "?",
        senderColor: stringToColor(senderName),
        subject: item.subject || "Kein Betreff",
        date,
        dateLabel: gueltig ? getDateLabel(date) : "",
        preview: cleanMailPreview(item.bodyPreview || ""),
        body: "",
        type: "normal",
        read: !!item.isRead,
        hasAttachments: item.hasAttachments ?? false,
        to: item.toRecipients ?? [],
        cc: item.ccRecipients ?? [],
        matchedContact: item.matchedContact ?? null,
        matchedContactIds: item.matchedContactIds ?? [],
        recognizedContact: item.recognizedContact ?? null,
        recognizedContactIds: item.recognizedContactIds ?? [],
        // BEWUSST ohne `?? []` — wie im Listen-Mapping: `undefined` heißt
        // „keine Annotation" und lässt die Befehlsleiste zurückfallen, ein
        // leeres Array ist die Aussage „nichts erkannt".
        senderCandidates: item.senderCandidates,
        isInternalSender: item.isInternalSender ?? false,
        parentFolderId: item.parentFolderId,
        srcMailbox: mailbox,
        meetingMessageType: item.meetingMessageType ?? null,
    };
}

/** Ungelesene unter den geladenen Termin-Mails — die Zahl am Ordner. */
export function ungeleseneTermine(mails: readonly TerminMail[]): number {
    return mails.reduce((n, m) => (m.read ? n : n + 1), 0);
}

/**
 * Zähler-Beschriftung. Solange der Posteingang nicht zu Ende durchgesehen ist
 * (`hasMore`), steht ein „+" dahinter — die Zahl ist dann eine Untergrenze und
 * soll auch so aussehen. Eine glatte Zahl wäre hier eine Behauptung.
 */
export function terminZaehlerLabel(unread: number, hasMore: boolean): string {
    if (unread <= 0) return "";
    const zahl = unread > 99 ? "99" : String(unread);
    return hasMore ? `${zahl}+` : zahl;
}

/**
 * Suche INNERHALB der Termine. Die globale Server-Suche kann hier nicht greifen
 * (sie sucht Postfächer, nicht diese Sicht) — statt die Eingabe verpuffen zu
 * lassen, wird sie lokal über Betreff, Absender, Vorschau und Termin-Art
 * angewandt. Gleiches Vorgehen wie bei den Vorgangs-Mails.
 */
export function matchesTerminSuche(mail: TerminMail, suchtext: string): boolean {
    const needle = suchtext.trim().toLowerCase();
    if (!needle) return true;
    return [
        mail.subject,
        mail.senderName,
        mail.senderEmail,
        mail.preview,
        terminArtLabel(mail.meetingMessageType),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
}

async function holeJson<T>(url: string): Promise<T> {
    const res = await fetch(url);
    if (!res.ok) {
        let detail = "";
        try {
            const body = await res.json();
            detail = typeof body?.message === "string" ? body.message : "";
        } catch {
            /* Antwort ohne JSON-Körper — der Status genügt. */
        }
        throw new Error(detail || `Termine konnten nicht geladen werden: ${res.status}`);
    }
    return res.json() as Promise<T>;
}

/**
 * Eine Seite des Termine-Ordners holen. `skip` ist der `nextSkip` der Vorseite
 * (Roh-Offset im Posteingang) — bewusst ungecacht, die Ansicht wird bei jedem
 * Öffnen frisch geholt.
 */
export async function ladeTermine(mailbox: string, skip = 0): Promise<TermineSeite> {
    const r = await holeJson<{ items: TerminMailDto[]; meta: UnreadMeta }>(
        apiUrl("mail/termine", { mailbox, top: 50, skip }),
    );
    return { mails: (r.items ?? []).map((i) => dtoZuTerminMail(i, mailbox)), meta: r.meta };
}
